import {
  Autocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";
import { ServiceOptions } from "./ServiceOptions";
import { useEffect, useRef, useState } from "react";
import { LocateMeIcon, SearchIcon } from "../../../icons";
import Loader from "../../../components/elements/loader";
import { size } from "lodash";
import { toast } from "../../../components/ui/Toast/useToast";
import { useLocation, useOptionSelection } from "../../../hooks/react-query/useNewService";
import { buildGaragesCalendar, clearSBStepStatusState, getTimeslotWindow, undoUntil } from "../serviceUtils";
import { SERVICE_BOOKING_STEPS } from "../../../config/constants";
import { geocode, RequestType, setKey } from "react-geocode";

export const BookingStepTwo = ({ state, dispatch }) => {
  // Step Two: Here the user chooses the types of services
  // he/she wants to book an appointment for based on the location input.

  // LOCAL STATE:
  const [locationArea, setLocationArea] = useState()
  const [isResolvingPlace, setIsResolvingPlace] = useState(false)
  const [undoLoading, setUndoLoading] = useState(false)
  const [inputValue, setInputValue] = useState("");
  const [haveCheckedOptions, setHaveCheckedOptions] = useState(false)
  const [crmFormNoGarages, setCrmFormNoGarages] = useState(false)
  
  // FETCH DATA:
  setKey("AIzaSyDXFLTkJcZ-Pyn5uI0KLzUoyPZ8Ay1dRJk");


  // LOCAL VARIABLES:
  const { mutate: location, isLoading: isLocationLoading } = useLocation()
  const { mutate: optionSelect, isLoading: isOptionSelectionLoading} = useOptionSelection()
  
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDXFLTkJcZ-Pyn5uI0KLzUoyPZ8Ay1dRJk",
    libraries: ["places"],
    language: "el",
    region: "GR",
  });

  // USE EFFECTS:
    
  useEffect(() => {
    if (state?.pickupCRMFormAddress?.area) {
      setInputValue(state.pickupCRMFormAddress.area);
    }
  }, [state?.pickupCRMFormAddress?.area]);

  // useEffect(() => {
  //   navigator.geolocation.getCurrentPosition(
  //     (position) => {
  //       // console.log("located",position)
  //     },
  //     () => {
  //       console.warn("Geolocation failed, using default center.");
  //     }
  //   );
  // }, []);

  useEffect(() => {
    if (!locationArea) return;
    
    const run = async () => {
      if(SERVICE_BOOKING_STEPS[state?.stepStatus] > SERVICE_BOOKING_STEPS.location) {
        clearSBStepStatusState({targetStepStatus: "location", dispatch})
        setHaveCheckedOptions(false)
        setCrmFormNoGarages(false)
        await handleRelocate()
      } else {
        setHaveCheckedOptions(false)
        setCrmFormNoGarages(false)
        await handleLocationSearch();
      }
    }

    run();
  }, [locationArea]);

  
  // HANDLERS & FUNCTIONS:

  const handleRelocate = async () => {
    try {
      setHaveCheckedOptions(false)
      setUndoLoading(true)
      await undoUntil({targetStep : "location", state, dispatch})
      handleLocationSearch()
    } catch(e) {
      console.warn(e)
      toast({
        variant: 'destructive',
        description: 'Κάτι πήγε στραβά',
      });
    } finally {
      setUndoLoading(false)
    }
  }

  const handlePlaceChanged = () => {
    setIsResolvingPlace(true);

    requestAnimationFrame(() => {
      const place = autocompleteRef.current?.getPlace?.();
      const value = inputRef.current?.value;

      const area =
        `${place?.address_components?.find((comp) => comp?.types?.includes("locality") || comp?.types?.includes("political"))?.long_name}, ${place?.address_components?.find((comp) => comp?.types?.includes("postal_code"))?.long_name ?? ""}` || value;

      if (area) {
        setInputValue(area);
        setLocationArea(area.replace(/,+\s*$/, ''));
        dispatch({
          type: 'SET_PICKUP_ADDRESS',
          option: 'area',
          value: place?.address_components?.find((comp) => comp?.types?.includes("locality") || comp?.types?.includes("political"))?.long_name,
        });
      }

      setIsResolvingPlace(false);
    });
  };

  const handleLocationSearch = () => {
    setHaveCheckedOptions(false)
    const payload = {
      location: locationArea,
    }
    location(
      {payload: payload, serviceFlowId:state?.serviceFlowId}, 
      {
        onSuccess : (response) => {
          if (response && response.exceptions) {
            if (size(response.exceptions) > 0) {
              const message = response?.exceptions?.location?.[0] || 'Παρουσιάστηκε κάποιο πρόβλημα';
              toast({
                variant: 'destructive',
                description: message,
              });
            }
          } else { // handle no exceptions
            dispatch({ type: "SET_STEPSTATUS", stepStatus: response?.name });
            const availableOptions = response?.inputs?.optionSelection?.options?.filter(opt => opt !== "AuthorizedMap")
            if(availableOptions.includes("PickupCRMForm") || availableOptions.includes("PickupAvis")) {
              if(response?.firstAvailableAppointments?.PickupAvis !== null){
                dispatch({type: "SET_SELECTED_PICKUP", selectedPickup:true})
                dispatch({ type: "SET_SELECTED_PAYMENT", selectedPayment: 'cash' });
              }
              const pickup = response?.pickupDeliveryCosts?.filter((method)=> method?.type === "Cash")?.[0]?.amount
              const loyalty = response?.pickupDeliveryCosts?.filter((method)=> method?.type === "Points")?.[0]?.amount
              const showInvoiceOption = response?.pickupDeliveryCosts?.filter((method)=> method?.type === "Cash")?.[0]?.availablePaymentDocumentTypes.includes("invoice")
              dispatch({ type: "SET_PICKUP_COST", pickupCost: pickup});
              dispatch({ type: "SET_LOYALTY_COST", loyaltyCost: loyalty});
              dispatch({type: "SET_LOCK_STEPPER_NEXT", lockStepperNext:false})
              dispatch({type: "SET_SHOW_INVOICE_OPTION", showInvoiceOption:showInvoiceOption})
            }
            dispatch({
              type:"SET_STEPTHREE_OPTIONS",
              option:"pickupOptions",
              value:availableOptions?.filter(opt => opt === "PickupCRMForm" || opt === "PickupAvis")
            })
            dispatch({
              type:"SET_STEPTHREE_OPTIONS",
              option:"CRMOptions",
              value:availableOptions?.filter(opt => opt === "CRMForm")
            })
            dispatch({
              type:"SET_STEPTHREE_OPTIONS",
              option:"availabilityOptions",
              value:availableOptions?.filter(opt => opt !== "PickupCRMForm" && opt !== "CRMForm" && opt !== "PickupAvis" && opt !== "AuthorizedMap")
            })
            if(size(response?.firstAvailableAppointments) > 0) {
              let dateFirstAvailable = null
              let datePickup = null
              if(response?.firstAvailableAppointments?.FirstAvailableAvis) {
                dateFirstAvailable = new Date(response?.firstAvailableAppointments?.FirstAvailableAvis?.startTime).toLocaleDateString('el-GR');
              }
              if(response?.firstAvailableAppointments?.PickupAvis) {
                datePickup = new Date(response?.firstAvailableAppointments?.PickupAvis?.startTime).toLocaleDateString('el-GR');
              }
              dispatch({ type: "SET_STEP_TWO_AVAILABILITY", stepTwoAvailableAppointments: {pickup:datePickup, firstAvailable: dateFirstAvailable}})
            }
            if(availableOptions?.length === 1 && ["CRMForm"].includes(availableOptions?.[0])) {
              const payload = { optionSelection: availableOptions?.[0] }
              try{
                setHaveCheckedOptions(false)
                optionSelect({payload, serviceFlowId:state?.serviceFlowId },
                {
                  onSuccess : (response) => {
                    if (response && response.exceptions) {
                      if (size(response.exceptions) > 0) {
                        toast({
                          variant: 'destructive',
                          description: 'Παρουσιάστηκε κάποιο πρόβλημα',
                        });
                      } 
                    } else { // handle no exceptions
                      const normalized = buildGaragesCalendar(
                        response?.inputs?.preferredGarage?.options,
                        response?.inputs?.preferredTimeslots?.options
                      );
                      const preferedTimeslots = response?.inputs?.preferredTimeslots?.options?.map((slot)=> { return {startTime: slot?.StartTime, endTime:slot?.EndTime} })
                      dispatch({ type: "SET_SELECTED_GARAGE", selectedGarage: response?.inputs?.preferredGarage?.options?.[0]})
                      dispatch({ type: "SET_CALENDAR_DATA", calendarData: preferedTimeslots});
                      dispatch({ type: "SET_GARAGE_CALENDAR_DATA", garageCalendarData: normalized})
                      dispatch({ type: "SET_STEPSTATUS", stepStatus: response?.name });
                      dispatch({ type: "SET_STEP_THREE", stepThree: "CRMForm"});
                      if(["CRMForm"].includes(availableOptions?.[0]) && normalized?.length > 0 ) {
                        dispatch({ type: "SET_STEP", step: state.step + 1 });
                      } else if (["CRMForm"].includes(availableOptions?.[0]) && normalized?.length === 0 ) {
                        setCrmFormNoGarages(true)
                      } else {
                        dispatch({ type: "SET_STEP", step: state.step + 1 });
                      }
                    }
                  }
                }
              )} catch(e) {
                console.warn(e)
                toast({
                  variant: 'destructive',
                  description: 'Παρουσιάστηκε κάποιο πρόβλημα',
                });
              }
            }
            if(availableOptions?.length === 1 && 
              response?.firstAvailableAppointments?.length !== 0  &&
              response?.firstAvailableAppointments !== null
              && ["FirstAvailableAvis","NearestAvis"].includes(availableOptions?.[0])) {
              const payload = { optionSelection: availableOptions?.[0] }
              try{
                optionSelect({payload, serviceFlowId:state?.serviceFlowId },
                {
                  onSuccess : (response) => {
                    if (response && response.exceptions) {
                      if (size(response.exceptions) > 0) {
                        toast({
                          variant: 'destructive',
                          description: 'Παρουσιάστηκε κάποιο πρόβλημα',
                        });
                      } 
                    } else { // handle no exceptions
                        const bookingSelectOptions =
                          response?.inputs?.bookingSelect?.options || [];
              
                        const bookingCalendarOptions =
                          response?.inputs?.bookingCalendar?.options || [];
              
                        const normalized = buildGaragesCalendar(
                          bookingSelectOptions || [],
                          bookingCalendarOptions
                        );
                        const preselectedDate = new Date(normalized?.[0]?.calendarData?.[0]?.startTime)
                        dispatch({ type: 'CLEAR_TIMESLOTS' })
                        dispatch({ type: "SET_GARAGE_CALENDAR_DATA", garageCalendarData: normalized})
                        dispatch({ type: "SET_CALENDAR_DATA", calendarData: bookingCalendarOptions});
                        dispatch({ type: "SET_SELECTED_GARAGE", selectedGarage: normalized?.[0]})
                        dispatch({ type: 'SET_SELECTED_DATE', value: preselectedDate.toDateString()})
                        dispatch({ 
                          type: "ADD_TIMESLOT", 
                          value: getTimeslotWindow({
                            slot: normalized?.[0]?.calendarData?.[0], 
                            dateKey: preselectedDate.toLocaleDateString('en-CA')
                          })
                        })
                        dispatch({ type: "SET_STEP_THREE", stepThree: "FirstAvailableAvis"});
                        dispatch({ type: "SET_STEPSTATUS", stepStatus: response?.name });
                        dispatch({ type: "SET_STEP", step: state.step + 1 });
                    } 
                  }
                }
              )} catch(e) {
                console.warn(e)
                toast({
                  variant: 'destructive',
                  description: 'Παρουσιάστηκε κάποιο πρόβλημα',
                });
              }
            } else {setHaveCheckedOptions(true)}
          }
        }
      })
  }

  const handleLocateMe = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        // fromLatLng(position?.coords?.latitude, position?.coords?.longitude)
          geocode(RequestType.LATLNG, `${position?.coords?.latitude},${position?.coords?.longitude}`, {
          location_type: "ROOFTOP", // Override location type filter for this request.
          enable_address_descriptor: true, // Include address descriptor in response.
        })
          .then(({ results }) => {
            const { city } = results[0].address_components.reduce(
              (acc, component) => {
                if (component.types.includes("locality"))
                  acc.city = component.long_name;
                else if (component.types.includes("administrative_area_level_1"))
                  acc.state = component.long_name;
                else if (component.types.includes("country"))
                  acc.country = component.long_name;
                return acc;
              },
              {}
            );
            // console.log(city, state, country);
            // console.log(address);
            setInputValue(city)
            // setLocationArea(city)
            
            requestAnimationFrame(() => {
                  if (inputRef.current) {
                    inputRef.current.focus();
                    const end = city.length;
                    inputRef.current.setSelectionRange?.(end, end);
                    // In some cases Autocomplete needs an input event to open suggestions
                    inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
                  }
                });

          })
          .catch(console.error);
        },
      () => {
        console.warn("Geolocation failed, using default center.");
      }
    );
  }

  if (!isLoaded) { 
    return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm">
      <Loader />
    </div>}

  
  return (
    <>
      {(undoLoading || isLocationLoading || isResolvingPlace || isOptionSelectionLoading) && <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <Loader />
        </div>
      }
      <div className="flex flex-row w-full max-sm:flex-col max-sm:items-center gap-6 min-w-[80%] ">
        <div className="flex flex-col gap-6 w-full items-start">
          <h4 className=''>Σε ποια περιοχή βρίσκεσαι;</h4>
          <div className="relative w-full"
            onKeyDownCapture={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
          >
            <SearchIcon width='24' height='24' color="black" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <Autocomplete
              onLoad={(ref) => (autocompleteRef.current = ref)}
              onPlaceChanged={handlePlaceChanged}
              options={{
                // types: ["address"],
                types: ["(regions)"],
                componentRestrictions: { country: "gr" }
              }}
            >
              <input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={"Αναζήτηση περιοχής ή ΤΚ"}
                className="
                  w-full
                  h-16
                  rounded-full
                  border
                  border-mildgrey
                  bg-white
                  pl-11
                  py-2
                  text-gray-800
                  placeholder-gray-400
                  focus:outline-none
                  focus:border-blue-500
                  focus:ring-2
                  focus:ring-blue-200"
              />
            </Autocomplete>
            {locationArea ? <button
              onClick={() => {
                setInputValue("");
                setLocationArea("");
              }}
              className="absolute right-3 top-1/2 mr-5 px-[5px] -translate-y-1/2 text-grenite bg-powdergrey rounded-full"
            >
              🞩
            </button> :  <button
              onClick={handleLocateMe}
              className="absolute right-3 top-1/2 mr-2 px-[5px] -translate-y-1/2"
            >
              <LocateMeIcon />
            </button>}
          </div>
          {state?.stepThreeOptions?.pickupOptions?.length > 0 && 
            <>
              <h4 className="pt-4">Πώς θέλεις να εξυπηρετηθείς;</h4>
              <ServiceOptions state={state} dispatch={dispatch}/>
            </>
          }
          {haveCheckedOptions && locationArea && [...state?.stepThreeOptions?.CRMOptions, ...state?.stepThreeOptions?.pickupOptions, ...state?.stepThreeOptions?.availabilityOptions]?.length === 1 &&
            (state?.stepThreeOptions?.CRMOptions?.length === 0 && (state?.stepTwoAvailableAppointments?.pickup === null && state?.stepTwoAvailableAppointments?.firstAvailable === null)) && 
              <p className='text-[#D4002A]'>Δεν υπάρχει διαθεσιμότητα για {locationArea}.</p>
            }
          {haveCheckedOptions && crmFormNoGarages && locationArea && 
              <p className='text-[#D4002A]'>Δεν υπάρχει διαθεσιμότητα για {locationArea}.</p>
            }
          {haveCheckedOptions && [...state?.stepThreeOptions?.CRMOptions, ...state?.stepThreeOptions?.pickupOptions, ...state?.stepThreeOptions?.availabilityOptions]?.length === 0 &&
            <p className='text-[#D60D0D]'>Οι συγκεκριμένες εργασίες δεν μπορούν να προγραμματιστούν μαζί. Μπορείς να κλείσεις ξεχωριστά ραντεβού για την κάθε μία.</p>
          }
        </div>
      </div>
    </>
  );
};
